<script setup>
import { computed } from 'vue'
import { gradesToLabels } from '@/util/gradesort'
import { BLURRED_TEXT_CLASSES, getPlaceholderText } from '@/util/placeholder_text'

const props = defineProps({
  grades: {
    type: Array,
    required: true
  },
  level: {
    type: String,
    default: ''
  },
  levelLabel: {
    type: String,
    default: ''
  },
  superPage: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: 'default' // 'default' or 'aligned-goals'
  },
  // When given, bubbles are drawn from the levels this goal has rather than from every anchor level
  levels: {
    type: Array,
    default: null
  },
  blurLock: {
    type: Boolean,
    default: false
  }
})

const ANCHOR_LEVELS = ['grade_level', 'approaching', 'developing']
const BLURRED_GRADE_LENGTH = 8

const PALETTES = {
  default: { text: 'text-blue-500', border: 'border-primary-500', selected: 'bg-primary-500', unselected: 'bg-blue-100' },
  'aligned-goals': { text: 'text-blue-400', border: 'border-blue-400', selected: 'bg-blue-400', unselected: 'bg-white' }
}

const isAlignedGoals = computed(() => props.variant === 'aligned-goals')
const palette = computed(() => PALETTES[props.variant] ?? PALETTES.default)
const bubbleLevels = computed(() => props.levels ?? ANCHOR_LEVELS)
const isLevelsGiven = computed(() => props.levels !== null)
const showBubbles = computed(() => isLevelsGiven.value || props.superPage)

const showLevel = computed(() => {
  if (isLevelsGiven.value) {
    return props.levels.length > 0
  }
  return props.level !== ''
})

const gradeLabel = computed(() => {
  if (props.blurLock) {
    return getPlaceholderText(BLURRED_GRADE_LENGTH)
  }
  const grades = gradesToLabels(props.grades)
  let label = `Grade ${
    (grades.length > 1 && grades[0] + '-' + grades[grades.length - 1]) ||
    grades[0]
  }`

  if (grades[0] === 'Birth-3' || grades[0] === 'Pre-K' || grades[0] === 'Adult Transition') {
    label = label.replace('Grade ', '')
  }
  return label
})

const gradeContainsAdultTransition = computed(() => {
  return props.grades.length > 1 && props.grades.includes('Adult-Transition')
})

const containerClasses = computed(() => {
  const spacing = isAlignedGoals.value ? ['pt-1'] : ['pt-4', 'pb-4', 'mb-[-6px]']
  const alignment = gradeContainsAdultTransition.value ? 'items-start' : 'items-center'
  return ['flex', 'gap-2', ...spacing, palette.value.text, alignment]
})

const bubblesGapClass = computed(() => {
  return isAlignedGoals.value ? 'gap-2.5' : 'gap-3.5'
})

const getBubbleClasses = (level) => {
  const size = isAlignedGoals.value ? ['h-3', 'w-3'] : ['h-3.5', 'w-3.5']
  const fill = level === props.level ? palette.value.selected : palette.value.unselected
  return ['bubble', 'rounded-[50%]', 'border-x', 'border-y', ...size, palette.value.border, fill, { 'blur-[5px]': props.blurLock }]
}
</script>
<template>
  <div :class="containerClasses">
    <div
      aria-hidden="true"
      class="relative flex items-center shrink-0"
      v-if="showLevel"
    >
      <div class="absolute inset-0 flex items-center" v-if="showBubbles && !blurLock">
        <div class="w-full border-t" :class="palette.border"></div>
      </div>
      <div v-if="showBubbles" class="relative flex" :class="bubblesGapClass">
        <div
          :key="bubbleLevel"
          :class="getBubbleClasses(bubbleLevel)"
          v-for="bubbleLevel of bubbleLevels"
        />
      </div>
    </div>
    <span
      class="text-[10px] font-bold shrink-0"
      :class="{ [BLURRED_TEXT_CLASSES]: blurLock }"
      :aria-hidden="blurLock || undefined"
      v-if="showLevel"
    >{{ levelLabel || level }}</span>
    <div
      v-if="variant !== 'aligned-goals'"
      class="text-right text-[10px] font-bold ml-auto"
      :class="{ 'shrink text-balance mt-[-4px]': gradeContainsAdultTransition, [BLURRED_TEXT_CLASSES]: blurLock }"
      :aria-hidden="blurLock || undefined"
    >{{ gradeLabel }}</div>
  </div>
</template>
